import React from 'react'
import { X, AlertTriangle } from 'lucide-react'

interface RiskDisclaimerPopupProps {
  isOpen: boolean
  onAccept: () => void
  onDecline: () => void
}

const RiskDisclaimerPopup: React.FC<RiskDisclaimerPopupProps> = ({ 
  isOpen, 
  onAccept, 
  onDecline 
}) => {
  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        {/* Modal */}
        <div className="bg-dark-800 rounded-2xl shadow-2xl max-w-2xl w-full h-[90vh] flex flex-col border border-dark-600">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 rounded-t-2xl flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="h-8 w-8 text-white" />
                <h2 className="text-2xl font-bold text-white">Risk Disclaimer</h2>
              </div>
              <button
                onClick={onDecline}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6 text-gray-300">
              <div className="bg-red-950/30 border border-red-700/30 rounded-lg p-4">
                <p className="text-red-200 font-semibold mb-2">
                  ⚠️ IMPORTANT: Please read this disclaimer carefully before proceeding
                </p>
              </div>

              <div className="space-y-4 text-sm leading-relaxed">
                <p>
                  <strong className="text-white">Investment Risks:</strong> Investments in securities markets are subject to market risks. 
                  The value of investments may go up or down depending on market conditions, economic factors, and company performance.
                </p>

                <p>
                  <strong className="text-white">No Guarantee:</strong> Past performance does not guarantee future results. 
                  All investment recommendations and analysis provided are for informational purposes only and should not be 
                  considered as personalized investment advice.
                </p>

                <p>
                  <strong className="text-white">Professional Consultation:</strong> You are advised to consult with a qualified 
                  financial advisor before making any investment decisions. Consider your financial situation, investment objectives, 
                  and risk tolerance before investing.
                </p>

                <p>
                  <strong className="text-white">Market Risks:</strong> The stock market is subject to risks including the loss of principal. Historical performance 
                  does not guarantee future results. All recommendations are based on our analysis and research, 
                  but markets can be unpredictable.
                </p>

                <p>
                  <strong className="text-white">SEBI Registration:</strong> AnalyzerView is a SEBI Registered Research Analyst 
                  (INH000023719). However, SEBI registration does not guarantee performance or returns.
                </p>

                <p>
                  <strong className="text-white">Loss of Capital:</strong> There is a risk of loss of your entire investment. 
                  Never invest money you cannot afford to lose. Diversify your investments and invest only surplus funds.
                </p>

                <p>
                  <strong className="text-white">Market Volatility:</strong> Securities markets can be highly volatile. 
                  Prices may fluctuate significantly in short periods due to various factors including economic news, 
                  company announcements, and market sentiment.
                </p>
              </div>

              <div className="bg-amber-950/30 border border-amber-700/30 rounded-lg p-4">
                <p className="text-amber-200 text-sm">
                  By clicking "I Accept", you acknowledge that you have read, understood, and agree to these risk disclosures. 
                  You also confirm that you are aware of the risks involved in securities investments.
                </p>
              </div>
            </div>
          </div>

          {/* Footer - Always visible at bottom */}
          <div className="bg-dark-750 px-6 py-4 rounded-b-2xl border-t border-dark-600 flex-shrink-0">
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={onDecline}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors font-medium"
              >
                I Do Not Accept
              </button>
              <button
                onClick={onAccept}
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-medium"
              >
                I Accept & Understand the Risks
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default RiskDisclaimerPopup